import { createContext, type ReactNode, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  emptySessionState,
  type RevisionedSessionState,
  type SessionCommandType,
  type SessionEvent,
  type SessionParticipantRole,
  type SessionState,
  type PrintHelp,
  type BlitzlesenFullscreenCommand,
} from "@calega/realtime";
import { io, type Socket } from "socket.io-client";
import { api } from "../lib/api";
import { createSessionRecovery } from "./session-recovery";
import { createCommandDelivery } from "./command-delivery";
import { restoreActiveSession } from "./active-session-restore";
import type { ExercisePauseState } from "@calega/content";

interface ActiveSession {
  id: string;
  code: string;
  learnerId: string;
  revision: number;
  expiresAt: string;
  participantRole: SessionParticipantRole;
}

interface RealtimeValue {
  whiteboardSocket: Socket | null;
  session: ActiveSession | null;
  state: SessionState;
  connected: boolean;
  synchronized: boolean;
  notice: string;
  printHelp: PrintHelp | null;
  requestPrintHelp(runId: string, itemKey: string): Promise<PrintHelp>;
  pauseExerciseDelivery(runId: string, paused: boolean): void;
  serverNow(): number;
  start(): Promise<void>;
  join(code: string): Promise<void>;
  end(): Promise<void>;
  restoreActive(): () => void;
  disconnect(): void;
  publishRoute(slug: string): void;
  publishExercise(payload: unknown): void;
  publishBlitzlesenFullscreen(payload: BlitzlesenFullscreenCommand): void;
}

const RealtimeContext = createContext<RealtimeValue | null>(null);

type ExerciseEnvelope = { exerciseType?: unknown; pageId?: unknown; runId?: unknown; state?: unknown };

function objectValue(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : null;
}

function sameJson(left: unknown, right: unknown) {
  return left === right || JSON.stringify(left) === JSON.stringify(right);
}

export function withoutGeneratedRun(payload: unknown) {
  const envelope = objectValue(payload);
  const state = objectValue(envelope?.state);
  if (!envelope || !state || state.run === undefined) return payload;
  const { run: _run, ...compactState } = state;
  return { ...envelope, state: compactState };
}

export function participantExerciseCommand(previousPayload: unknown, nextPayload: unknown): SessionCommandType {
  const previous = objectValue(previousPayload) as ExerciseEnvelope | null;
  const next = objectValue(nextPayload) as ExerciseEnvelope | null;
  const previousState = objectValue(previous?.state);
  const nextState = objectValue(next?.state);
  if (!previous || !next || !previousState || !nextState
    || previous.exerciseType !== next.exerciseType || previous.pageId !== next.pageId) return "exercise.control";
  for (const key of ["runId", "run", "index", "endsAt", "result"] as const) {
    const previousValue = key === "runId" ? previous.runId ?? previousState.runId : previousState[key];
    const nextValue = key === "runId" ? next.runId ?? nextState.runId : nextState[key];
    if (!sameJson(previousValue, nextValue)) return "exercise.control";
  }
  for (const key of ["phase", "answers", "feedback", "ratings", "score", "maxScore", "submitted",
    "stage", "submitAttempts", "visibleDivisionSteps", "placedObjectIds", "wrongAttempts", "wrongObjectId", "correct", "total"] as const) {
    if (!sameJson(previousState[key], nextState[key])) return "exercise.answer";
  }
  return "exercise.input";
}

const inputStateKeys = new Set([
  "value", "values", "draft", "correctionCount", "intermediateValues", "events",
  "selectedIndices", "tappedObjectIndices", "vowelSelections", "splits", "selection", "cuts",
  "assignments", "selectedTokenId", "selectedPos", "selectedTokenIds", "selectedChunkId", "selectedRole", "selectedObjectId",
]);

export function facilitatorExerciseCommand(previousPayload: unknown, nextPayload: unknown): SessionCommandType {
  if (objectValue(nextPayload)?.exerciseType === "blitzlesen") return "exercise.control";
  const type = participantExerciseCommand(previousPayload, nextPayload);
  if (type !== "exercise.input") return type;
  const previousState = objectValue(objectValue(previousPayload)?.state)!;
  const nextState = objectValue(objectValue(nextPayload)?.state)!;
  // Unknown settings remain individual control commands rather than being bundled.
  for (const key of new Set([...Object.keys(previousState), ...Object.keys(nextState)])) {
    if (!inputStateKeys.has(key) && !sameJson(previousState[key], nextState[key])) return "exercise.control";
  }
  return "exercise.input";
}

export function hashForSessionRoute(route: string) {
  const prefix = "individual-assignment:";
  return route.startsWith(prefix)
    ? `#/individual-assignment/${encodeURIComponent(route.slice(prefix.length))}`
    : `#/page/${encodeURIComponent(route)}`;
}

export function RealtimeProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<ActiveSession | null>(null);
  const [state, setState] = useState<SessionState>(() => emptySessionState());
  const [connected, setConnected] = useState(false);
  const [peerReady, setPeerReady] = useState(false);
  const [pendingCommands, setPendingCommands] = useState(0);
  const [notice, setNotice] = useState("");
  const [printHelp, setPrintHelp] = useState<PrintHelp | null>(null);
  const [deliveryNotice, setDeliveryNotice] = useState("");
  const socketRef = useRef<Socket | null>(null);
  const recoveryRef = useRef<ReturnType<typeof createSessionRecovery> | null>(null);
  const deliveryRef = useRef<ReturnType<typeof createCommandDelivery> | null>(null);
  const cancelRestoreRef = useRef<(() => void) | null>(null);
  const activeSessionIdRef = useRef<string | null>(null);
  const revisionRef = useRef(0);
  const stateRef = useRef<SessionState>(emptySessionState());
  const serverOffsetRef = useRef(0);

  function commit(next: RevisionedSessionState, displayed: SessionState) {
    revisionRef.current = next.revision;
    stateRef.current = next.state;
    setState(displayed);
    const route = displayed.route;
    if (route) {
      const hash = hashForSessionRoute(route);
      if (window.location.hash !== hash) window.location.hash = hash;
    }
  }

  function resetLocalState() {
    setPrintHelp(null);
    const empty = emptySessionState();
    revisionRef.current = 0;
    stateRef.current = empty;
    serverOffsetRef.current = 0;
    setState(empty);
  }

  async function connect(active: ActiveSession) {
    cancelRestoreRef.current?.();
    activeSessionIdRef.current = active.id;
    deliveryRef.current?.dispose();
    setDeliveryNotice("");
    setPendingCommands(0);
    setPeerReady(false);
    recoveryRef.current?.dispose();
    recoveryRef.current = null;
    socketRef.current?.disconnect();
    resetLocalState();
    setNotice("");
    const socket = io("/learning", { path: "/socket.io", withCredentials: true, autoConnect: false });
    socketRef.current = socket;
    setConnected(false);
    setSession(active);
    const delivery = createCommandDelivery({
      send: (command, complete) => {
        recoveryRef.current?.invalidate();
        socket.timeout(10_000).emit("session:command", { sessionId: active.id, ...command, payload: withoutGeneratedRun(command.payload) }, complete);
      },
      recover: (delay) => recoveryRef.current?.recover(delay),
      change: (current, displayed, pending) => { commit(current, displayed); setPendingCommands(pending); },
      notice: setDeliveryNotice,
    });
    deliveryRef.current = delivery;
    const recovery = createSessionRecovery(socket, active.id, {
      revision: () => revisionRef.current,
      applySync: (sync) => {
        setPrintHelp(sync.printHelp ?? null);
        serverOffsetRef.current = sync.serverNow - Date.now();
        delivery.sync(sync);
      },
      connected: (value) => { setConnected(value); delivery.connection(value); },
      ready: setPeerReady,
      notice: setNotice,
    });
    recoveryRef.current = recovery;
    socket.on("subscription:restricted", () => window.dispatchEvent(new Event("calepia:subscription-restricted")));
    socket.on("session:event", (event: SessionEvent, acknowledge?: (response: { revision: number }) => void) => {
      if (socketRef.current !== socket) return;
      recovery.invalidate();
      delivery.event(event);
      acknowledge?.({ revision: revisionRef.current });
    });
    socket.on("exercise:pause", (state: ExercisePauseState) => {
      if (socketRef.current === socket) window.dispatchEvent(new CustomEvent("calepia:pause-state", { detail: state }));
    });
    socket.on("session:ended", (event: { sessionId?: string; reason?: "ended" | "timeout" }) => {
      if (socketRef.current !== socket || event.sessionId !== active.id) return;
      setNotice(event.reason === "timeout" ? "Die Sitzung wurde nach 120 Minuten automatisch beendet." : "Die gemeinsame Sitzung wurde beendet.");
      disconnect();
    });
    socket.on("session:print-help", (help: PrintHelp) => {
      if (socketRef.current === socket) setPrintHelp(help);
    });
    socket.connect();
  }

  useEffect(() => () => {
    cancelRestoreRef.current?.();
    recoveryRef.current?.dispose();
    socketRef.current?.disconnect();
    deliveryRef.current?.dispose();
  }, []);

  useEffect(() => {
    if (!session || !connected) return;
    const remaining = new Date(session.expiresAt).getTime() - (Date.now() + serverOffsetRef.current);
    const timeout = window.setTimeout(() => {
      const action = session.participantRole === "FACILITATOR" ? "end" : "leave";
      const socket = socketRef.current;
      api(`/sessions/${session.id}/${action}`, { method: "POST" }).catch(() => undefined).finally(() => {
        if (socketRef.current === socket) disconnect();
      });
    }, Math.max(0, remaining + 50));
    return () => window.clearTimeout(timeout);
  }, [session, connected]);

  /*
   * Socket.IO erzeugt nach einem Netzabbruch eine neue Serververbindung. Die
   * Wiederherstellung tritt deshalb erneut dem Raum bei und lädt fehlende
   * Revisionen. Sie prüft auch ausbleibende Bestätigungen bei offener Verbindung.
   */

  async function start() {
    cancelRestoreRef.current?.();
    const active = await api<ActiveSession>("/sessions", { method: "POST" });
    await connect(active);
  }

  async function join(code: string) {
    cancelRestoreRef.current?.();
    const active = await api<ActiveSession>("/sessions/join", { method: "POST", body: JSON.stringify({ code }) });
    await connect(active);
  }

  function restoreActive() {
    cancelRestoreRef.current?.();
    const cancel = restoreActiveSession<ActiveSession>({
      load: (signal) => api("/sessions/active", { signal }),
      restore: (active) => { void connect(active); },
      notice: setNotice,
    });
    cancelRestoreRef.current = cancel;
    return cancel;
  }

  async function end() {
    if (session) {
      const action = session.participantRole === "FACILITATOR" ? "end" : "leave";
      await api(`/sessions/${session.id}/${action}`, { method: "POST" });
    }
    setNotice("");
    disconnect();
  }

  function disconnect() {
    cancelRestoreRef.current?.();
    activeSessionIdRef.current = null;
    deliveryRef.current?.dispose();
    deliveryRef.current = null;
    setDeliveryNotice("");
    setPendingCommands(0);
    recoveryRef.current?.dispose();
    recoveryRef.current = null;
    socketRef.current?.disconnect();
    socketRef.current = null;
    setConnected(false);
    setPeerReady(false);
    setSession(null);
    resetLocalState();
  }

  function publish(type: SessionCommandType, payload: unknown) {
    if (!session || activeSessionIdRef.current !== session.id) return;
    deliveryRef.current?.enqueue(type, payload);
  }

  const value = useMemo<RealtimeValue>(() => ({
    whiteboardSocket: socketRef.current,
    session,
    state,
    connected,
    synchronized: connected && peerReady && pendingCommands === 0,
    notice: deliveryNotice || notice || (pendingCommands > 0 ? `${pendingCommands} Änderung(en) warten auf Bestätigung.` : ""),
    printHelp,
    pauseExerciseDelivery: (runId, paused) => {
      const envelope = deliveryRef.current?.displayed().exercise as { runId?: string; state?: { runId?: string } } | null;
      if ((envelope?.runId ?? envelope?.state?.runId) === runId) deliveryRef.current?.suspend(paused);
    },
    requestPrintHelp: (runId, itemKey) => new Promise((resolve, reject) => {
      const socket = socketRef.current;
      if (!socket || !session || !connected || !peerReady) return reject(new Error("Die gemeinsame Sitzung ist noch nicht verbunden."));
      socket.timeout(10_000).emit("session:print-help", { sessionId: session.id, runId, itemKey }, (error: Error | null, result: { ok?: boolean; help?: PrintHelp; error?: { message?: string } }) => {
        if (error || !result?.ok || !result.help) return reject(new Error(result?.error?.message ?? "Die Druckschrift-Hilfe konnte nicht synchronisiert werden."));
        if (socketRef.current !== socket) return reject(new Error("Die gemeinsame Sitzung wurde beendet."));
        setPrintHelp(result.help); resolve(result.help);
      });
    }),
    serverNow: () => Date.now() + serverOffsetRef.current,
    start,
    join,
    end,
    restoreActive,
    disconnect,
    publishRoute: (slug) => {
      if (session?.participantRole !== "PARTICIPANT") publish("route.set", { slug });
    },
    publishExercise: (payload) => {
      const previous = deliveryRef.current?.displayed().exercise ?? stateRef.current.exercise;
      const type = session?.participantRole === "PARTICIPANT"
        ? participantExerciseCommand(previous, payload)
        : facilitatorExerciseCommand(previous, payload);
      publish(type, payload);
    },
    publishBlitzlesenFullscreen: (payload) => publish("blitzlesen.fullscreen", payload),
  }), [session, state, connected, peerReady, pendingCommands, notice, deliveryNotice, printHelp]);

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

export function useRealtime() {
  const context = useContext(RealtimeContext);
  if (!context) throw new Error("RealtimeProvider fehlt.");
  return context;
}
